import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Check, Loader2, Mic, Sparkles, Square, X } from "lucide-react";
import { toast } from "sonner";
import { tokenFor } from "@/lib/presence";

const ROOT = `${process.env.REACT_APP_BACKEND_URL}/api/memo-assist`;
const auth = () => ({ Authorization: `Bearer ${tokenFor(window.location.pathname)}` });
const errMsg = (e) => e?.response?.data?.detail || "Something went wrong — try again";
const btn = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition-colors disabled:opacity-50";

function useRecorder(onAudio) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  useEffect(() => { if (!rec) return undefined; const id = setInterval(() => setSecs((s) => s + 1), 1000); return () => clearInterval(id); }, [rec]);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ["audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const mr = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); onAudio(new Blob(chunks, { type: mr.mimeType || "audio/webm" })); };
      mr.start(); setSecs(0); setRec(mr);
    } catch { toast.error("Allow microphone access to talk your memo"); }
  };
  const stop = () => { rec?.stop(); setRec(null); };
  return { recording: !!rec, secs, start, stop };
}

/** Mic + "Fix wording" under any memo box: speech drops into the box; the AI fix is shown for approval before it replaces anything. */
export function MemoAssist({ value, onChange, id = "memo" }) {
  const [busy, setBusy] = useState("");
  const [fix, setFix] = useState(null);
  const latest = useRef(value);
  latest.current = value;
  const send = async (blob) => {
    setBusy("voice");
    try {
      const fd = new FormData();
      fd.append("audio", blob, blob.type.includes("mp4") ? "memo.mp4" : "memo.webm");
      const { data } = await axios.post(`${ROOT}/transcribe`, fd, { headers: auth() });
      if (!data.text) toast.message("Didn't catch any words — try again a little closer to the mic");
      else onChange(latest.current.trim() ? `${latest.current.trim()} ${data.text}` : data.text);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const mic = useRecorder(send);
  const polish = async () => {
    setBusy("fix");
    try {
      const { data } = await axios.post(`${ROOT}/polish`, { text: value }, { headers: auth() });
      if (data.changed) setFix(data.text); else toast.success("Looks good — nothing to fix");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  return (
    <div className="space-y-2" data-testid={`${id}-assist`}>
      <div className="flex flex-wrap items-center gap-2">
        {mic.recording
          ? <button type="button" onClick={mic.stop} data-testid={`${id}-mic-stop`} className={`${btn} border-red-400/60 text-red-300 bg-red-500/10`}><Square size={11} className="fill-current" /> Stop · {mic.secs}s</button>
          : <button type="button" onClick={mic.start} disabled={!!busy} data-testid={`${id}-mic`} className={`${btn} border-white/15 text-white/70 hover:border-[#D4AF37]/60 hover:text-[#F4D88A]`}>
              {busy === "voice" ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} {busy === "voice" ? "Writing it out…" : "Talk"}</button>}
        <button type="button" onClick={polish} disabled={!!busy || mic.recording || !value.trim()} data-testid={`${id}-fix`} className={`${btn} border-white/15 text-white/70 hover:border-[#D4AF37]/60 hover:text-[#F4D88A]`}>
          {busy === "fix" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {busy === "fix" ? "Fixing…" : "Fix wording"}</button>
      </div>
      {fix !== null && (
        <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-3 space-y-2" data-testid={`${id}-fix-preview`}>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#F4D88A]/80">Suggested wording</div>
          <p className="text-sm text-white/90 whitespace-pre-wrap" data-testid={`${id}-fix-text`}>{fix}</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => { onChange(fix); setFix(null); }} data-testid={`${id}-fix-use`} className={`${btn} border-[#D4AF37] bg-[#D4AF37] text-black font-semibold`}><Check size={12} /> Use it</button>
            <button type="button" onClick={() => setFix(null)} data-testid={`${id}-fix-keep`} className={`${btn} border-white/15 text-white/70`}><X size={12} /> Keep mine</button>
          </div>
        </div>)}
    </div>
  );
}
